import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Share, Download, Zap, Smartphone, Wifi, Monitor } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { getPublicPageTopPadding } from '@/lib/layout-utils';

const InstallApp = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "forkast - Family Meal Planning",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Any",
    "description": "Install forkast on your device for quick access to AI-powered family meal planning. Works offline with home screen access.",
    "url": "https://forkast.app/install",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className={`min-h-screen bg-background ${getPublicPageTopPadding()}`}>
      <SEOHead 
        title="Install forkast - Add to Home Screen for Quick Access" 
        description="Install forkast on your device for the best experience. Quick access from your home screen, works offline, and no app store required." 
        jsonLd={jsonLd} 
      />
      
      {/* Hero Section */}
      <section className="pb-12 md:py-20 bg-gradient-to-br from-background via-sage/10 to-terracotta/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-sage/20 text-sage px-3 py-1 rounded-full text-sm font-medium mb-4">
              <Download className="h-4 w-4" />
              Works Offline
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-playfair text-charcoal mb-6">
              Install forkast <br />
              <span className="text-terracotta">on your device</span>
            </h1>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto text-lg mb-8">
              Get instant access to your meal plans and recipes right from your home screen. 
              No app store needed - forkast works like a native app on any device.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-8 md:py-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
              Why install forkast?
            </h2>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto">
              Get the full app experience without downloading from an app store
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            {/* Lightning Fast */}
            <div className="text-center p-6 bg-gradient-to-br from-sage/5 to-sage/10 rounded-2xl border border-sage/20">
              <div className="w-16 h-16 bg-sage text-cream rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold font-playfair text-charcoal mb-3">
                Lightning Fast
              </h3>
              <p className="text-muted-foreground font-inter">
                Launches instantly from your home screen with smooth, 
                responsive interactions optimized for your device.
              </p>
            </div>

            {/* Works Offline */}
            <div className="text-center p-6 bg-gradient-to-br from-terracotta/5 to-terracotta/10 rounded-2xl border border-terracotta/20">
              <div className="w-16 h-16 bg-terracotta text-cream rounded-full flex items-center justify-center mx-auto mb-4">
                <Wifi className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold font-playfair text-charcoal mb-3">
                Works Offline
              </h3>
              <p className="text-muted-foreground font-inter">
                Access your recipes and meal plans even without an internet connection. 
                Perfect for grocery shopping or cooking.
              </p>
            </div>

            {/* Quick Access */}
            <div className="text-center p-6 bg-gradient-to-br from-golden/5 to-golden/10 rounded-2xl border border-golden/20">
              <div className="w-16 h-16 bg-golden-dark text-cream rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold font-playfair text-charcoal mb-3">
                Instant Access
              </h3>
              <p className="text-muted-foreground font-inter">
                One tap from your home screen to access recipes, meal planner, 
                and shopping lists. No browser needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Instructions */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-cream/20 via-background to-sunset/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
              How to install
            </h2>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto">
              Choose your device to see installation instructions
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            {/* iOS Safari */}
            <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-charcoal text-cream rounded-xl flex items-center justify-center">
                  <Smartphone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold font-playfair text-charcoal">iPhone / iPad</h3>
                  <p className="text-sm text-muted-foreground">Safari browser</p>
                </div>
              </div>
              <ol className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-sage text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span className="text-muted-foreground">Tap the <Share className="inline h-4 w-4 mx-1" /> <strong className="text-foreground">Share</strong> button in Safari</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-sage text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span className="text-muted-foreground">Scroll down and tap <strong className="text-foreground">"Add to Home Screen"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-sage text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span className="text-muted-foreground">Tap <strong className="text-foreground">"Add"</strong> to confirm</span>
                </li>
              </ol>
            </div>

            {/* Android Chrome */}
            <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-terracotta text-cream rounded-xl flex items-center justify-center">
                  <Smartphone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold font-playfair text-charcoal">Android</h3>
                  <p className="text-sm text-muted-foreground">Chrome browser</p>
                </div>
              </div>
              <ol className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-terracotta text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span className="text-muted-foreground">Tap the <strong className="text-foreground">⋮ menu</strong> in Chrome</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-terracotta text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span className="text-muted-foreground">Tap <strong className="text-foreground">"Install app"</strong> or "Add to Home Screen"</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-terracotta text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span className="text-muted-foreground">Tap <strong className="text-foreground">"Install"</strong> to confirm</span>
                </li>
              </ol>
            </div>

            {/* Desktop */}
            <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-golden-dark text-cream rounded-xl flex items-center justify-center">
                  <Monitor className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold font-playfair text-charcoal">Desktop</h3>
                  <p className="text-sm text-muted-foreground">Chrome / Edge</p>
                </div>
              </div>
              <ol className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-golden-dark text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span className="text-muted-foreground">Look for the <Download className="inline h-4 w-4 mx-1" /> <strong className="text-foreground">install icon</strong> in your address bar</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-golden-dark text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span className="text-muted-foreground">Click <strong className="text-foreground">"Install"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-golden-dark text-cream rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span className="text-muted-foreground">forkast will open in its own window</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="py-8 md:py-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
                Everything you love about forkast,<br />
                optimized for your device
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-sage text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">Recipe Collection</h4>
                    <p className="text-muted-foreground font-inter text-sm">Import and manage your family recipes from any website</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-terracotta text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">Meal Planning</h4>
                    <p className="text-muted-foreground font-inter text-sm">Plan breakfast, lunch, and dinner for the whole week</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-golden-dark text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">Shopping Lists</h4>
                    <p className="text-muted-foreground font-inter text-sm">Auto-generated lists organized by category</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-sage text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">Family Collaboration</h4>
                    <p className="text-muted-foreground font-inter text-sm">Share recipes and meal plans with your whole family</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-terracotta text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">AI Suggestions</h4>
                    <p className="text-muted-foreground font-inter text-sm">Get personalized meal recommendations</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-golden-dark text-cream rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold font-playfair text-charcoal mb-1">Wellness Insights</h4>
                    <p className="text-muted-foreground font-inter text-sm">Track your family's eating patterns and nutritional balance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 md:py-12 pb-24 md:pb-12 bg-gradient-to-br from-sage/5 to-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
              Ready to get started?
            </h2>
            <p className="text-muted-foreground font-inter mb-8">
              Create your free account and start planning meals for your family today
            </p>
            <Link to="/auth/register">
              <Button 
                size="lg" 
                className="px-8 py-4 text-lg bg-gradient-to-r from-sage to-terracotta hover:from-sage/90 hover:to-terracotta/90 text-white font-inter font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Smartphone className="mr-3 h-5 w-5" />
                Get Started Free
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground font-inter mt-4">
              Free to use • No credit card required • Works on all devices
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstallApp;
